import { Helmet } from "react-helmet-async";
import { useHreflangTags, HreflangLink } from "@/hooks/useHreflangTags";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { ReactNode } from "react";

const BASE_URL = "https://prelozim.sk";
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-image.png`;

interface SEOHeadProps {
  title: string;
  description: string;
  ogType?: string;
  ogImage?: string;
  children?: ReactNode;
}

const getLocale = (lang: Language): string => {
  const localeMap: Record<Language, string> = {
    sk: 'sk_SK',
    en: 'en_GB',
    de: 'de_DE',
    uk: 'uk_UA',
    ru: 'ru_RU',
    es: 'es_ES',
  };
  return localeMap[lang] || 'sk_SK';
};

// Helper component to render hreflang links outside Helmet's direct children mapping issue
function HreflangLinks({ links, defaultUrl }: { links: HreflangLink[]; defaultUrl: string }) {
  return (
    <Helmet>
      {links.map(link => (
        <link 
          key={link.hreflang} 
          rel="alternate" 
          hrefLang={link.hreflang} 
          href={link.href} 
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={defaultUrl} />
    </Helmet>
  );
}

export function SEOHead({ 
  title, 
  description, 
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  children 
}: SEOHeadProps) {
  const { generateHreflangLinks, getCanonicalUrl, getDefaultUrl } = useHreflangTags();
  const { language } = useLanguage();
  const hreflangLinks = generateHreflangLinks();
  const canonicalUrl = getCanonicalUrl(language);
  const defaultUrl = getDefaultUrl();
  const locale = getLocale(language);

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <html lang={language} />
        <title>{title}</title>
        <meta name="description" content={description} />
        
        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Prelozim.sk" />
        <meta property="og:locale" content={locale} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={title} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* Additional children (JSON-LD schemas, etc.) */}
        {children}
      </Helmet>
      
      {/* Hreflang links in separate Helmet to avoid mapping issues */}
      <HreflangLinks links={hreflangLinks} defaultUrl={defaultUrl} />
    </>
  );
}
